import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { getNeighborhoods } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, EyeOff, Home, MapPin, Loader2, Navigation } from 'lucide-react';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    neighborhood: '',
    address: '',
    phone: '',
    zipcode: '',
    showCustomNeighborhood: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [detectedLocation, setDetectedLocation] = useState(null);

  useEffect(() => {
    const fetchNeighborhoods = async () => {
      try {
        const data = await getNeighborhoods();
        setNeighborhoods(data);
      } catch (error) {
        console.error('Failed to fetch neighborhoods:', error);
      }
    };
    fetchNeighborhoods();
  }, []);

  // GPS Location Detection
  const detectNeighborhood = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setDetectingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Use OpenStreetMap Nominatim for reverse geocoding (free, no API key required)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=16&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'en',
                'User-Agent': 'NeighborBorrow/1.0'
              }
            }
          );
          
          const data = await response.json();
          
          if (data && data.address) {
            // Extract neighborhood info from various possible fields
            const neighborhood = 
              data.address.neighbourhood ||
              data.address.suburb ||
              data.address.district ||
              data.address.city_district ||
              data.address.quarter ||
              data.address.village ||
              data.address.town ||
              data.address.city ||
              'Unknown Area';
            
            // Extract zipcode/postal code
            const zipcode = data.address.postcode || '';
            
            // Build a formatted address
            const addressParts = [];
            if (data.address.house_number) addressParts.push(data.address.house_number);
            if (data.address.road) addressParts.push(data.address.road);
            if (data.address.city || data.address.town) {
              addressParts.push(data.address.city || data.address.town);
            }
            if (data.address.postcode) addressParts.push(data.address.postcode);
            
            const fullAddress = addressParts.join(', ');
            
            setDetectedLocation({
              neighborhood,
              address: fullAddress,
              zipcode,
              latitude,
              longitude
            });
            
            // Check if detected neighborhood exists in our list
            const matchingNeighborhood = neighborhoods.find(
              n => n.toLowerCase() === neighborhood.toLowerCase()
            );
            
            setFormData(prev => ({
              ...prev,
              neighborhood: matchingNeighborhood || neighborhood,
              address: fullAddress,
              zipcode: zipcode,
              showCustomNeighborhood: !matchingNeighborhood
            }));
            
            toast.success(`Location detected: ${neighborhood}${zipcode ? ` (${zipcode})` : ''}`);
          } else {
            toast.error('Could not determine your neighborhood');
          }
        } catch (err) {
          console.error('Reverse geocoding error:', err);
          toast.error('Failed to get neighborhood from location');
        } finally {
          setDetectingLocation(false);
        }
      },
      (err) => {
        setDetectingLocation(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            toast.error('Location permission denied. Please enable location access.');
            break;
          case err.POSITION_UNAVAILABLE:
            toast.error('Location information is unavailable.');
            break;
          case err.TIMEOUT:
            toast.error('Location request timed out.');
            break;
          default:
            toast.error('An error occurred while getting location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (!formData.neighborhood) {
      setError('Please select your neighborhood');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        neighborhood: formData.neighborhood,
        address: formData.address || undefined,
        phone: formData.phone || undefined,
        zipcode: formData.zipcode || undefined,
        latitude: detectedLocation?.latitude || undefined,
        longitude: detectedLocation?.longitude || undefined
      });
      toast.success('Welcome to NeighborBorrow!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      <Helmet>
        <title>Sign Up | NeighborBorrow</title>
        <meta name="description" content="Create your NeighborBorrow account and start borrowing or lending items in your neighborhood. Free to join." />
      </Helmet>
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.pexels.com/photos/1084374/pexels-photo-1084374.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Neighborhood"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#FDFBF7] to-transparent"></div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center space-x-2 mb-8" data-testid="logo-link">
            <div className="w-12 h-12 bg-[#D97757] rounded-full flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-semibold text-2xl text-[#2C3329]">NeighborBorrow</span>
          </Link>

          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-8">
            <h1 className="font-heading text-2xl font-semibold text-[#2C3329] text-center mb-2">
              Join your community
            </h1>
            <p className="text-[#596652] text-center mb-6">
              Create an account to start borrowing and lending
            </p>

            {error && (
              <div className="bg-[#E53935]/10 text-[#E53935] px-4 py-3 rounded-xl mb-6 text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="full_name" className="text-[#2C3329]">Full Name</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                  placeholder="John Doe"
                  data-testid="fullname-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-[#2C3329]">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                  placeholder="you@example.com"
                  data-testid="email-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="neighborhood" className="text-[#2C3329]">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Neighborhood
                  </Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={detectNeighborhood}
                    disabled={detectingLocation}
                    className="text-[#D97757] hover:text-[#C56547] hover:bg-[#D97757]/10 h-auto py-1 px-2"
                    data-testid="detect-location-button"
                  >
                    {detectingLocation ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        Detecting...
                      </>
                    ) : (
                      <>
                        <Navigation className="w-3.5 h-3.5 mr-1" />
                        Use my location
                      </>
                    )}
                  </Button>
                </div>
                
                {detectedLocation && (
                  <div className="mb-2 p-2 bg-[#8C9A76]/10 rounded-lg text-sm text-[#596652]">
                    <span className="font-medium">Detected:</span> {detectedLocation.neighborhood}
                  </div>
                )}
                
                <Select 
                  value={formData.neighborhood || undefined} 
                  onValueChange={(value) => {
                    if (value === 'custom') {
                      setFormData({ ...formData, neighborhood: '', showCustomNeighborhood: true });
                    } else {
                      setFormData({ ...formData, neighborhood: value, showCustomNeighborhood: false });
                    }
                  }}
                >
                  <SelectTrigger className="border-[#EBE8DF] rounded-xl" data-testid="neighborhood-select">
                    <SelectValue placeholder="Select your neighborhood" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Add detected neighborhood if not in list */}
                    {detectedLocation && 
                     !neighborhoods.includes(detectedLocation.neighborhood) && 
                     formData.neighborhood === detectedLocation.neighborhood && (
                      <SelectItem value={detectedLocation.neighborhood}>
                        {detectedLocation.neighborhood} (Detected)
                      </SelectItem>
                    )}
                    {neighborhoods.map((hood) => (
                      <SelectItem key={hood} value={hood}>{hood}</SelectItem>
                    ))}
                    <SelectItem value="custom">Other (Enter custom)</SelectItem>
                  </SelectContent>
                </Select>
                {formData.showCustomNeighborhood && (
                  <Input
                    name="customNeighborhood"
                    type="text"
                    value={formData.neighborhood}
                    onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                    className="mt-2 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                    placeholder="Enter your neighborhood"
                    data-testid="custom-neighborhood-input"
                  />
                )}
              </div>

              <div>
                <Label htmlFor="address" className="text-[#2C3329]">Address (optional)</Label>
                <Input
                  id="address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleChange}
                  className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                  placeholder="123 Main St"
                  data-testid="address-input"
                />
              </div>

              <div>
                <Label htmlFor="zipcode" className="text-[#2C3329]">
                  Zipcode / Postal Code
                  {detectedLocation?.zipcode && (
                    <span className="ml-2 text-xs text-[#8C9A76] font-normal">(Auto-detected)</span>
                  )}
                </Label>
                <Input
                  id="zipcode"
                  name="zipcode"
                  type="text"
                  value={formData.zipcode}
                  onChange={handleChange}
                  className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                  placeholder="10001"
                  data-testid="zipcode-input"
                />
                <p className="text-xs text-[#596652] mt-1">
                  Helps neighbors find items near them
                </p>
              </div>

              <div>
                <Label htmlFor="phone" className="text-[#2C3329]">Phone (optional)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                  placeholder="(555) 123-4567"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-[#2C3329]">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="border-[#EBE8DF] rounded-xl pr-10 focus:ring-2 focus:ring-[#8C9A76]/50"
                    placeholder="••••••••"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#596652] hover:text-[#2C3329]"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-[#2C3329]">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="mt-1 border-[#EBE8DF] rounded-xl focus:ring-2 focus:ring-[#8C9A76]/50"
                  placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547] py-3"
                data-testid="register-button"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <p className="text-center text-[#596652] mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-[#D97757] hover:text-[#C56547] font-medium" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
